import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { SegmentFilters } from "../api/queries";
import { zonedToMs } from "../time/parse";
import type { ZoneMode } from "../time/format";

export type ListFilter = "all" | "show" | "track";
export type ListenedFilter = "all" | "yes" | "no";

/**
 * Everything the Filter panel can narrow a listing by, in one value.
 *
 * Shared by the public archive and the review queue because the panels differ only in which
 * rows they draw: the archive offers the tracker, review offers the three flags an editor
 * works through, and both offer the kind and the tombstones. One shape means one translation
 * into query parameters (`segmentQuery`), which is the part that must not drift.
 */
export interface Filters {
  kind: ListFilter;
  listened: ListenedFilter;
  /** With `listened: "no"`, the highest percentage still counted as unlistened. */
  listenedMax: number;
  /** Whether to include audio that has aged out of the archive. Off, they are hidden. */
  expired: boolean;
  /** Review only: detected but not yet reviewed. */
  provisional: boolean;
  /** Review only: no scheduled event matched confidently enough to link. */
  unlinked: boolean;
  /** Review only: approved by retention against the clock, not by a person. */
  auto: boolean;
  /** Inclusive first day of the range, `YYYY-MM-DD` in the displayed zone. Empty for none. */
  from: string;
  /** Inclusive last day of the range, `YYYY-MM-DD` in the displayed zone. Empty for none. */
  to: string;
}

export const NO_FILTERS: Filters = {
  kind: "all",
  listened: "all",
  listenedMax: 0,
  expired: false,
  provisional: false,
  unlinked: false,
  auto: false,
  from: "",
  to: "",
};

/**
 * What the public archive opens on: shows, which are what someone came for. A three-minute
 * jukebox track is not what a visitor is looking for, and there are thousands of them per
 * show; the Jukebox tab is a step away either way.
 */
export const ARCHIVE_DEFAULT: Filters = { ...NO_FILTERS, kind: "show" };

/**
 * What the review queue opens on: the rows retention will approve if nobody looks at them.
 *
 * Here rather than beside the queue because the Filter trigger needs it too -- lighting up is
 * "you have narrowed this past where it started", and in review it starts here.
 */
export const REVIEW_DEFAULT: Filters = { ...NO_FILTERS, kind: "show", provisional: true };

/** The instant a `YYYY-MM-DD` day begins in `zone`. */
function dayStart(date: string, zone: string): string | null {
  const ms = zonedToMs(`${date} 00:00`, zone);
  return ms === null ? null : new Date(ms).toISOString();
}

/**
 * The instant a `YYYY-MM-DD` day ends: the next day's start, so "to" includes the day named.
 *
 * The next day is stepped on the calendar rather than by adding 24 hours, because a day
 * containing a daylight-saving change is 23 or 25 hours long and the offset is applied after.
 */
function dayEnd(date: string, zone: string): string | null {
  const next = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(next.getTime())) return null;
  next.setUTCDate(next.getUTCDate() + 1);
  return dayStart(next.toISOString().slice(0, 10), zone);
}

/**
 * The panel's value as the segment listing's query parameters.
 *
 * Each control narrows independently, so several at once intersect rather than union --
 * which is what a row of checkboxes means everywhere else, and is why "Unverified" and
 * "Auto-approved" together come back empty: retention flips the state when it approves.
 * "Expired" is the exception and reads the other way, as its own comment below says.
 *
 * Nulls rather than omissions for anything off, so a caller spreading this over its own
 * defaults cannot leave a stale parameter behind.
 *
 * `zone` is needed because the date range is two calendar days, and a calendar day is only
 * an instant once a zone says so. It is the displayed zone rather than the browser's: the
 * rows are dated in station time when the toggle says station time, and a range that meant
 * the browser's midnight would cut them hours away from where the reader sees the day break.
 */
export function segmentQuery(filters: Filters, zone: string): SegmentFilters {
  return {
    kind: filters.kind === "all" ? null : filters.kind,
    listened: filters.listened === "all" ? null : filters.listened,
    listenedMax: filters.listened === "no" ? filters.listenedMax : null,
    // The one control that widens rather than narrows: tombstones are out of every listing
    // until it is ticked, so a search comes back with things that can actually be played.
    // `false` and not null when off -- the server's default includes them.
    expired: filters.expired ? null : false,
    state: filters.provisional ? "provisional" : null,
    unlinked: filters.unlinked ? true : null,
    autoApproved: filters.auto ? true : null,
    start: filters.from ? dayStart(filters.from, zone) : null,
    end: filters.to ? dayEnd(filters.to, zone) : null,
  };
}

/**
 * How many of the panel's controls are off `base`; any of them lights its trigger.
 *
 * `base` is the listing's own opening state, not `NO_FILTERS`, so review mode's shows-and-
 * unverified start reads as unnarrowed -- it is what the queue is, not a choice made in it.
 */
export function activeCount(filters: Filters, base: Filters = NO_FILTERS): number {
  return [
    filters.kind !== base.kind,
    filters.listened !== base.listened,
    filters.expired !== base.expired,
    filters.provisional !== base.provisional,
    filters.unlinked !== base.unlinked,
    filters.auto !== base.auto,
  ].filter(Boolean).length;
}

/**
 * The tracklist's search box, debounced, as the listing last sent it.
 *
 * Published at all because the player's queue is the filtered listing: pressing play while
 * searching for "Nucleus" plays Nucleus, and a term kept in the list's own state would be the
 * one part of the filter the queue never heard about.
 *
 * Its own store rather than a field on the persisted one below: a search is a moment and not a
 * preference, and routing it through `persist` would write localStorage on every keystroke.
 */
export const useListQuery = create<{ query: string; setQuery: (query: string) => void }>()(
  (set) => ({
    query: "",
    setQuery: (query) => set({ query }),
  }),
);

interface Prefs {
  /** Defaults to the viewer's own zone; the toggle is in user settings. */
  zoneMode: ZoneMode;
  listFilters: Filters;
  setZoneMode: (mode: ZoneMode) => void;
  setListFilters: (filters: Filters) => void;
}

export const usePrefs = create<Prefs>()(
  persist(
    (set) => ({
      zoneMode: "local",
      listFilters: ARCHIVE_DEFAULT,
      setZoneMode: (zoneMode) => set({ zoneMode }),
      setListFilters: (listFilters) => set({ listFilters }),
    }),
    {
      name: "jtarchive.prefs",
      // Only the two display preferences are persisted. Anything derived from the API
      // (the station's zone, the segment list) would go stale in localStorage and is
      // cheaper to re-fetch than to invalidate.
      partialize: (state) => ({ zoneMode: state.zoneMode, listFilters: state.listFilters }),
      // A panel gained controls since the value was written, so the stored object is
      // missing them -- and a missing `listenedMax` is a slider with no position rather
      // than a slider at zero.
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<Prefs>),
        listFilters: { ...ARCHIVE_DEFAULT, ...(persisted as Partial<Prefs>)?.listFilters },
      }),
    },
  ),
);
